function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

export function renderWaterClarityPanel(containerEl, model) {
  const progressWidth = Math.max(0, Math.min(100, Number(model.displayedLoadProgress) || 0));
  const progressFillStyle = model.isLoading
    ? [
      "height:100%",
      `width:${progressWidth}%`,
      "border-radius:999px",
      "background:linear-gradient(90deg, #0f766e 0%, #14b8a6 55%, #67e8f9 100%)",
      "box-shadow:0 0 0 1px rgba(15,118,110,0.15) inset",
      "transition:width 180ms ease",
    ].join(";")
    : [
      "height:100%",
      `width:${progressWidth}%`,
      "border-radius:999px",
      "background:linear-gradient(90deg, #0f766e 0%, #14b8a6 100%)",
      "opacity:0.9",
      "transition:width 220ms ease, opacity 220ms ease",
    ].join(";");

  const sourceRows = model.sources.length
    ? model.sources.map((source) => `
      <div class="card" style="padding:8px 10px; margin-top:8px;">
        <div style="display:flex; justify-content:space-between; gap:8px; align-items:flex-start;">
          <div>
            <div class="lm-title" style="margin:0;">${esc(source.name)}</div>
            <div class="small">${esc(source.kind)} · ${esc(source.priority)}</div>
          </div>
          <div class="small" style="white-space:nowrap;">${esc(source.state)}</div>
        </div>
        <div class="small" style="margin-top:6px;">${esc(source.notes)}</div>
        <div class="small" style="margin-top:6px;"><a href="${esc(source.url)}" target="_blank" rel="noreferrer">Open source</a></div>
      </div>
    `).join("")
    : `<div class="small" style="margin-top:8px;">No sources configured for that state.</div>`;

  const classRows = model.classes.map((item) => `
    <div style="display:grid; grid-template-columns:14px 1fr; gap:8px; align-items:start; margin-top:8px;">
      <div style="width:14px; height:14px; border-radius:999px; background:${esc(item.color)}; margin-top:2px;"></div>
      <div>
        <div class="small"><strong>${esc(item.label)}</strong> · ${esc(item.threshold)}</div>
        <div class="small">${esc(item.note)}</div>
      </div>
    </div>
  `).join("");

  const fieldRows = model.fields.map((field) => `
    <div style="margin-top:6px;">
      <div class="small"><strong>${esc(field.name)}</strong></div>
      <div class="small">${esc(field.description)}</div>
    </div>
  `).join("");

  const buildRows = model.buildSteps.map((step, index) => `
    <div class="small" style="margin-top:${index ? "8px" : "0"};">${index + 1}. ${esc(step)}</div>
  `).join("");

  containerEl.innerHTML = `
    <div class="card" style="padding:10px;">
      <div class="lm-title">Water Clarity</div>
      <div class="small" style="margin-top:6px;">Planning scaffold for historical inland-lake clarity, tuned for bathy lidar acquisition screening.</div>

      <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
        <label class="small" for="wc-state">Focus state</label>
        <select id="wc-state">
          ${model.states.map((state) => `<option value="${esc(state)}" ${state === model.state ? "selected" : ""}>${esc(state)}</option>`).join("")}
        </select>
      </div>

      <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
        <label class="small" for="wc-month">Planning month</label>
        <select id="wc-month">
          ${model.months.map((month) => `<option value="${esc(month.value)}" ${month.value === model.month ? "selected" : ""}>${esc(month.label)}</option>`).join("")}
        </select>
      </div>

      <div style="margin-top:8px;">
        <div
          aria-hidden="true"
          style="height:8px; border-radius:999px; background:#dbe4ec; overflow:hidden; box-shadow:inset 0 0 0 1px rgba(148,163,184,0.18);"
        >
          <div style="${progressFillStyle}"></div>
        </div>
        <div class="small" style="margin-top:4px; display:flex; justify-content:space-between; gap:8px;">
          <span>${esc(model.loadLabel)}</span>
          <span>${esc(`${Math.round(progressWidth)}%`)}</span>
        </div>
      </div>

      <div class="small" style="margin-top:8px;"><strong>Status:</strong> ${esc(model.status)}</div>
      <div class="small" style="margin-top:6px;">${esc(model.summary)}</div>
    </div>

    <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
      <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">Source Stack</summary>
      <div style="margin-top:8px;">
        <div class="small">Recommended starting sources for ${esc(model.state)} and adjacent cross-state coverage.</div>
        ${sourceRows}
      </div>
    </details>

    <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
      <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">Normalized Output</summary>
      <div style="margin-top:8px;">
        <div class="small">Suggested fields for the first app-ready lake polygon layer.</div>
        ${fieldRows}
      </div>
    </details>

    <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
      <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">Clarity Classes</summary>
      <div style="margin-top:8px;">
        <div class="small">Transparent planning bins for historical Secchi medians.</div>
        ${classRows}
      </div>
    </details>

    <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
      <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">Build Path</summary>
      <div style="margin-top:8px;">
        ${buildRows}
      </div>
    </details>
  `;
}
